/** Synth instruments for the built-in stations. Every sound is made live with Web Audio; no samples. */
export function midiToHz(midi: number): number {
  return 440 * 2 ** ((midi - 69) / 12);
}

export interface Kit {
  kick(t: number, level?: number): void;
  clap(t: number, level?: number): void;
  hat(t: number, open?: boolean, level?: number): void;
  shaker(t: number, level?: number): void;
  /** Gankogui-style double bell: high or low. */
  bell(t: number, high: boolean): void;
  bass(t: number, midi: number, length: number): void;
  /** Plucked highlife guitar note. */
  pluck(t: number, midi: number, level?: number): void;
  keys(t: number, midis: readonly number[], length: number, level?: number): void;
  organ(t: number, midis: readonly number[], length: number): void;
  /** Amapiano log drum: a pitched thump that drops. */
  logDrum(t: number, midi: number): void;
  /** Drill 808 with an optional slide. */
  sub(t: number, midi: number, length: number, slideTo?: number): void;
  lead(t: number, midi: number, length: number): void;
}

function envelope(ctx: AudioContext, t: number, peak: number, attack: number, decay: number): GainNode {
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  return gain;
}

function tone(ctx: AudioContext, out: AudioNode, type: OscillatorType, hz: number, t: number, peak: number, attack: number, decay: number): OscillatorNode {
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(hz, t);
  const gain = envelope(ctx, t, peak, attack, decay);
  osc.connect(gain).connect(out);
  osc.start(t);
  osc.stop(t + attack + decay + 0.05);
  return osc;
}

let noiseBuffer: AudioBuffer | null = null;
function noise(ctx: AudioContext, out: AudioNode, t: number, peak: number, decay: number, filterHz: number, type: BiquadFilterType): void {
  if (!noiseBuffer || noiseBuffer.sampleRate !== ctx.sampleRate) {
    noiseBuffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
  }
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer;
  const filter = ctx.createBiquadFilter();
  filter.type = type;
  filter.frequency.value = filterHz;
  src.connect(filter).connect(envelope(ctx, t, peak, 0.002, decay)).connect(out);
  src.start(t);
  src.stop(t + decay + 0.05);
}

export function createKit(ctx: AudioContext, out: AudioNode): Kit {
  return {
    kick(t, level = 1) {
      const osc = tone(ctx, out, 'sine', 140, t, 0.9 * level, 0.003, 0.28);
      osc.frequency.exponentialRampToValueAtTime(42, t + 0.14);
    },
    clap(t, level = 1) {
      noise(ctx, out, t, 0.35 * level, 0.14, 1500, 'bandpass');
      noise(ctx, out, t + 0.012, 0.25 * level, 0.1, 1800, 'bandpass');
    },
    hat(t, open = false, level = 1) {
      noise(ctx, out, t, 0.12 * level, open ? 0.22 : 0.04, 8000, 'highpass');
    },
    shaker(t, level = 1) {
      noise(ctx, out, t, 0.07 * level, 0.05, 6000, 'bandpass');
    },
    bell(t, high) {
      const hz = high ? 1320 : 990;
      tone(ctx, out, 'square', hz, t, 0.05, 0.001, 0.12);
      tone(ctx, out, 'sine', hz * 2.76, t, 0.03, 0.001, 0.08);
    },
    bass(t, midi, length) {
      tone(ctx, out, 'triangle', midiToHz(midi), t, 0.45, 0.01, length);
      tone(ctx, out, 'sine', midiToHz(midi - 12), t, 0.35, 0.01, length);
    },
    pluck(t, midi, level = 1) {
      tone(ctx, out, 'square', midiToHz(midi), t, 0.07 * level, 0.002, 0.22);
      tone(ctx, out, 'triangle', midiToHz(midi), t, 0.1 * level, 0.002, 0.3);
    },
    keys(t, midis, length, level = 1) {
      for (const m of midis) {
        tone(ctx, out, 'sine', midiToHz(m), t, 0.08 * level, 0.01, length);
        tone(ctx, out, 'triangle', midiToHz(m) * 2, t, 0.02 * level, 0.01, length * 0.6);
      }
    },
    organ(t, midis, length) {
      for (const m of midis) {
        tone(ctx, out, 'square', midiToHz(m), t, 0.025, 0.05, length);
        tone(ctx, out, 'sine', midiToHz(m) * 1.003, t, 0.06, 0.05, length);
      }
    },
    logDrum(t, midi) {
      const osc = tone(ctx, out, 'sine', midiToHz(midi) * 1.5, t, 0.55, 0.004, 0.35);
      osc.frequency.exponentialRampToValueAtTime(midiToHz(midi), t + 0.06);
    },
    sub(t, midi, length, slideTo) {
      const osc = tone(ctx, out, 'sine', midiToHz(midi), t, 0.7, 0.005, length);
      if (slideTo !== undefined) osc.frequency.exponentialRampToValueAtTime(midiToHz(slideTo), t + length * 0.8);
    },
    lead(t, midi, length) {
      tone(ctx, out, 'sawtooth', midiToHz(midi), t, 0.04, 0.01, length);
    },
  };
}
